import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  type CatalogHotkeyNodeKind,
  type HotkeyNode,
  useFunctionKeys,
} from '@/hooks/use-catalog-hotkeys';
import { useCategoriesFlat } from '@/hooks/use-categories';
import { ICON_OPTIONS } from './icon-options';
import { IconUpload } from './icon-upload';
import { useHotkeyIcons } from '@/hooks/use-hotkey-icons';
import { CategoryItemPicker } from './category-item-picker';
import { ItemSearchPicker } from './item-search-picker';
import { extractApiError } from '@/lib/api-error';
import { isAxiosError } from 'axios';
import { useT } from '@/i18n/use-t';

export type FormSubmit =
  | { mode: 'create'; input: CreateInput }
  | { mode: 'update'; id: string; patch: UpdatePatch };

type CreateInput = {
  parentId: string | null;
  sortOrder: number;
  label: string;
  kind: CatalogHotkeyNodeKind;
  payload: string | null;
  iconName: string | null;
  categoryId: string | null;
};

type UpdatePatch = {
  expectedVersion: number;
  label?: string;
  kind?: CatalogHotkeyNodeKind;
  payload?: string | null;
  iconName?: string | null;
  categoryId?: string | null;
};

/**
 * Edit/create form. Two modes:
 *   - editing an existing node → PATCH (expectedVersion mandatory)
 *   - creating a new node under a parent → POST
 *
 * Cross-field rules mirror the spec / contract:
 *   group    → payload disabled, forced to null
 *   item     → payload is a catalog item id, chosen by searching this
 *              tenant's catalogue (RL-586)
 *   function → payload is a server-published function key (select)
 */
export function NodeEditForm({
  mode,
  selected,
  parentId,
  defaultSortOrder,
  onSubmit,
  onDelete,
  onAddItems,
}: {
  mode: 'edit' | 'create';
  selected: HotkeyNode | null;
  parentId: string | null;
  defaultSortOrder: number;
  onSubmit: (next: FormSubmit) => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
  /** RFL-206: add catalog items as child item-nodes of a group node. */
  onAddItems?: (groupId: string, items: { id: string; name: string }[]) => Promise<void>;
}) {
  const t = useT();
  const functionKeys = useFunctionKeys();
  const categories = useCategoriesFlat();
  const [kind, setKind] = useState<CatalogHotkeyNodeKind>('group');
  const [label, setLabel] = useState('');
  const [payload, setPayload] = useState<string>('');
  const [iconName, setIconName] = useState<string>('');
  // VL-260 — the uploaded set, offered beside the shipped icon names.
  const customIcons = useHotkeyIcons().data ?? [];
  const [categoryId, setCategoryId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  // H-137: track unsaved edits so a tree refetch that bumps the
  // selected node's version (e.g. a concurrent edit by another admin)
  // doesn't silently wipe what the user is typing.
  const [dirty, setDirty] = useState(false);
  const lastInit = useRef<{ mode: string; id: string | undefined } | null>(null);

  // Reset state when selection changes (or mode flips). H-118 —
  // keying on `selected?.version` too so the form re-initialises after
  // OUR OWN successful PATCH (which bumps the row's version + clears
  // `dirty`), refreshing the stale `expectedVersion`. H-137 — but if
  // the user has unsaved edits (`dirty`) and only the version changed
  // (a different node identity is a genuine selection change), we keep
  // their edits rather than discarding them; the next save then 409s
  // on the stale version and surfaces via `error` (H-136), which is the
  // correct "you raced another editor" signal.
  useEffect(() => {
    const idChanged =
      lastInit.current === null ||
      lastInit.current.id !== selected?.id ||
      lastInit.current.mode !== mode;
    if (!idChanged && dirty) return; // preserve in-progress edits across external refetches
    setError(null);
    if (mode === 'edit' && selected !== null) {
      setKind(selected.kind);
      setLabel(selected.label);
      setPayload(selected.payload ?? '');
      setIconName(selected.iconName ?? '');
      setCategoryId(selected.categoryId ?? '');
    } else {
      setKind('group');
      setLabel('');
      setPayload('');
      setIconName('');
      setCategoryId('');
    }
    setDirty(false);
    lastInit.current = { mode, id: selected?.id };
  }, [mode, selected?.id, selected?.version, dirty]);

  // Group nodes cannot have a payload; force-clear if the user
  // switches kind.
  useEffect(() => {
    if (kind === 'group' && payload !== '') setPayload('');
  }, [kind, payload]);

  async function handleSubmit(): Promise<void> {
    setError(null);
    if (label.trim().length === 0) {
      setError(t('hotkeys.node.labelRequired', 'Label is required'));
      return;
    }
    const normalisedPayload =
      kind === 'group' ? null : payload.trim() === '' ? null : payload.trim();
    const normalisedIcon = iconName.trim() === '' ? null : iconName.trim();
    // Category binding is a group-only concept; force-clear for other kinds.
    const normalisedCategoryId = kind === 'group' && categoryId !== '' ? categoryId : null;
    if (kind !== 'group' && normalisedPayload === null) {
      setError(
        kind === 'item'
          ? t('hotkeys.node.itemRequired', 'Choose the catalog item this button adds')
          : t('hotkeys.node.payloadRequired', '{kind} nodes require a payload').replace(
              '{kind}',
              kind,
            ),
      );
      return;
    }
    // H-136 — surface server-side errors (409 stale version, 422
    // missing item, 400 cross-field, etc.) via the existing `error`
    // state. Without this catch the rejection bubbles up as an
    // unhandled promise rejection and the user sees nothing.
    try {
      if (mode === 'create') {
        await onSubmit({
          mode: 'create',
          input: {
            parentId,
            sortOrder: defaultSortOrder,
            label: label.trim(),
            kind,
            payload: normalisedPayload,
            iconName: normalisedIcon,
            categoryId: normalisedCategoryId,
          },
        });
      } else if (selected !== null) {
        await onSubmit({
          mode: 'update',
          id: selected.id,
          patch: {
            expectedVersion: selected.version,
            label: label.trim(),
            kind,
            payload: normalisedPayload,
            iconName: normalisedIcon,
            categoryId: normalisedCategoryId,
          },
        });
      }
      // H-137: a successful save is no longer "in progress" — clear the
      // dirty flag so the post-save refetch (new version) re-initialises
      // the form and refreshes expectedVersion (H-118).
      setDirty(false);
    } catch (err) {
      // RL-586 — Back Office's own message ("catalog item … not found",
      // "stale version", the failing field) and HTTP status, not axios's
      // generic "Request failed with status code 422". The shared helper
      // already includes status in its generic 5xx fallback.
      const message = extractApiError(err);
      const status = isAxiosError(err) ? err.response?.status : undefined;
      setError(
        status === undefined || message.includes(`(${status})`)
          ? message
          : `${message} (${status})`,
      );
    }
  }

  return (
    <form
      className="flex h-full flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
    >
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium">
          {mode === 'create'
            ? t('hotkeys.node.newTitle', 'New node')
            : t('hotkeys.node.editTitle', 'Edit node')}
        </h3>
        {mode === 'edit' && selected !== null && (
          <span className="text-xs text-muted-foreground">
            v{selected.version} · {selected.id}
          </span>
        )}
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="hk-kind">{t('hotkeys.node.kind', 'Kind')}</Label>
        <select
          id="hk-kind"
          className="rounded-md border bg-background px-3 py-1.5 text-sm"
          value={kind}
          onChange={(e) => {
            const next = e.target.value as CatalogHotkeyNodeKind;
            setDirty(true);
            // An item id is not a function key and vice versa: a payload never
            // survives a change of kind.
            if (next !== kind) setPayload('');
            setKind(next);
          }}
        >
          <option value="group">
            {t('hotkeys.node.kindGroup', 'group — drills into children')}
          </option>
          <option value="item">
            {t('hotkeys.node.kindItem', 'item — adds catalog item to basket')}
          </option>
          <option value="function">
            {t('hotkeys.node.kindFunction', 'function — runs a dispatch key')}
          </option>
        </select>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="hk-label">{t('hotkeys.node.label', 'Label')}</Label>
        <Input
          id="hk-label"
          value={label}
          onChange={(e) => {
            setDirty(true);
            setLabel(e.target.value);
          }}
          maxLength={120}
          required
        />
      </div>

      {kind === 'group' && (
        <div className="grid gap-1.5">
          <Label htmlFor="hk-category">
            {t('hotkeys.node.category', 'Catalog category (optional)')}
          </Label>
          <select
            id="hk-category"
            className="rounded-md border bg-background px-3 py-1.5 text-sm"
            value={categoryId}
            onChange={(e) => {
              setDirty(true);
              setCategoryId(e.target.value);
            }}
          >
            <option value="">{t('catalog.common.noneOption', '— none —')}</option>
            {categories.flat.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            {t(
              'hotkeys.node.categoryHint',
              'Bind a category to quickly add its items as child buttons below.',
            )}
          </p>
        </div>
      )}

      {/* RFL-206: quick-add items from the bound category. Only once the
          group exists (edit mode) and a category is saved on it. */}
      {mode === 'edit' &&
        selected !== null &&
        selected.kind === 'group' &&
        selected.categoryId !== null &&
        onAddItems !== undefined && (
          <CategoryItemPicker
            categoryId={selected.categoryId}
            onAdd={(items) => onAddItems(selected.id, items)}
          />
        )}

      {kind === 'item' && (
        <div className="grid gap-1.5">
          <Label htmlFor="hk-payload-item">{t('hotkeys.node.item', 'Catalog item')}</Label>
          <ItemSearchPicker
            value={payload}
            onChange={(item) => {
              setDirty(true);
              setPayload(item?.id ?? '');
              // Name the button after the item unless the operator already did.
              if (item !== null && label.trim() === '') setLabel(item.name);
            }}
          />
        </div>
      )}

      {kind === 'function' && (
        <div className="grid gap-1.5">
          <Label htmlFor="hk-payload-fn">{t('hotkeys.node.functionKey', 'Function key')}</Label>
          <select
            id="hk-payload-fn"
            className="rounded-md border bg-background px-3 py-1.5 text-sm"
            value={payload}
            onChange={(e) => {
              setDirty(true);
              setPayload(e.target.value);
            }}
          >
            <option value="">{t('catalog.common.selectOption', '— select —')}</option>
            {functionKeys.data?.keys.map((k) => (
              <option key={k.key} value={k.key}>
                {/* VL-460: translated where a row exists, else the English label. */}
                {t(`hotkeys.functionKey.${k.key}`, k.label)} ({k.key})
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="grid gap-1.5">
        <Label htmlFor="hk-icon">{t('hotkeys.node.icon', 'Icon')}</Label>
        <select
          id="hk-icon"
          className="rounded-md border bg-background px-3 py-1.5 text-sm"
          value={iconName}
          onChange={(e) => {
            setDirty(true);
            setIconName(e.target.value);
          }}
        >
          <option value="">{t('catalog.common.noneOption', '— none —')}</option>
          {ICON_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
          {/*
            VL-260 — uploaded glyphs sit in their own group under the `custom:`
            namespace. Separate rather than merged into the list above, because
            the two resolve differently on the till: a bare name against the
            icon set in the terminal's binary, a `custom:` name against the
            synced set. Mixing them would hide that a slug can never shadow a
            shipped glyph, or be shadowed by one.
          */}
          {customIcons.length > 0 && (
            <optgroup label={t('hotkeys.node.customIcons', 'Custom (uploaded)')}>
              {customIcons.map((icon) => (
                <option key={icon.slug} value={`custom:${icon.slug}`}>
                  {icon.name} (custom:{icon.slug})
                </option>
              ))}
            </optgroup>
          )}
        </select>
        <IconUpload
          onPicked={(next) => {
            setDirty(true);
            setIconName(next);
          }}
        />
      </div>

      {error !== null && (
        <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </p>
      )}

      <div className="mt-auto flex items-center gap-2">
        <Button type="submit">
          {mode === 'create'
            ? t('hotkeys.node.create', 'Create node')
            : t('catalog.common.saveChanges', 'Save changes')}
        </Button>
        {mode === 'edit' && selected !== null && onDelete !== undefined && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button type="button" variant="destructive">
                {t('catalog.common.delete', 'Delete')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {(selected.kind === 'group'
                    ? t('hotkeys.node.deleteTitleGroup', 'Delete "{label}" and all its children?')
                    : t('hotkeys.node.deleteTitle', 'Delete "{label}"?')
                  ).replace('{label}', selected.label)}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    'hotkeys.node.deleteWarning',
                    'This cannot be undone. The deletion is audited; on a group node, every descendant is removed and one audit row is written per node.',
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={() => void onDelete()}>
                  {t('catalog.common.delete', 'Delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </form>
  );
}
